import { createApi } from '@reduxjs/toolkit/query/react';
import type {
    ConfirmMutualFeedbackRemovalApiResponse,
    ConfirmMutualFeedbackRemovalApiRequest,
    TransactionFeedbackRemovalRequest,
    ValidateCreationApiResponse,
    CreateFeedbackRemovalApiResponse,
    CreateFeedbackRemovalApiRequest,
    ValidateConfirmationApiResponse,
    ConfirmationValidationRequest,
    DenyMututalFeedbackRemovalApiResponse,
    DenyMutualFeedbackRemovalApiRequest,
    FeedbackRemovalDetailsRequest,
    FeedbackRemovalApiResponse
} from 'tradera/state/services/types/webapi-member-generated';
import { isomorphicWebApiBaseQuery } from 'tradera/state/services/utils/webapi-base-query';
import { isServer } from 'tradera/utils/nextjs';
import qs from 'qs';

export const feedbackRemovalApi = createApi({
    reducerPath: 'feedbackRemovalApi',
    baseQuery: isomorphicWebApiBaseQuery,
    tagTypes: ['FeedbackRemoval'],
    endpoints: (builder) => ({
        getFeedbackRemovalCreation: builder.query<
            ValidateCreationApiResponse,
            TransactionFeedbackRemovalRequest
        >({
            query: ({ transactionId }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/member/feedback-removal/creation/validation?transactionId=${transactionId}`;
            },
            providesTags: ['FeedbackRemoval']
        }),
        getFeedbackRemovalConfirmationValidation: builder.query<
            ValidateConfirmationApiResponse,
            ConfirmationValidationRequest
        >({
            query: ({ id }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return `${baseUrl}/member/feedback-removal/confirmation/validation?id=${id}`;
            }
        }),
        createFeedbackRemoval: builder.mutation<
            CreateFeedbackRemovalApiResponse,
            CreateFeedbackRemovalApiRequest
        >({
            query: ({ comment, transactionId }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                const query = qs.stringify({ comment, transactionId });
                return {
                    url: `${baseUrl}/member/feedback-removal/create?${query}`,
                    method: 'POST'
                };
            }
        }),
        getFeedbackRemovalDetails: builder.query<
            FeedbackRemovalApiResponse,
            FeedbackRemovalDetailsRequest
        >({
            query: ({ feedbackRemovalRequestId }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return {
                    url: `${baseUrl}/member/feedback-removal-details?feedbackRemovalRequestId=${feedbackRemovalRequestId}`,
                    method: 'GET'
                };
            }
        }),
        confirmFeedbackRemoval: builder.mutation<
            ConfirmMutualFeedbackRemovalApiResponse,
            ConfirmMutualFeedbackRemovalApiRequest
        >({
            query: ({ feedbackRemovalRequestId }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return {
                    url: `${baseUrl}/member/feedback-removal/confirm`,
                    method: 'POST',
                    body: { feedbackRemovalRequestId }
                };
            }
        }),
        denyFeedbackRemoval: builder.mutation<
            DenyMututalFeedbackRemovalApiResponse,
            DenyMutualFeedbackRemovalApiRequest
        >({
            query: ({ feedbackRemovalRequestId }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return {
                    url: `${baseUrl}/member/feedback-removal/deny`,
                    method: 'POST',
                    body: { feedbackRemovalRequestId }
                };
            }
        }),
        getFeedbackRemovalTransaction: builder.query<
            FeedbackRemovalApiResponse,
            TransactionFeedbackRemovalRequest & { showDeleted?: boolean }
        >({
            query: ({ transactionId, showDeleted }) => {
                const baseUrl = isServer ? '' : '/api/webapi';
                return {
                    url: `${baseUrl}/member/feedbacks?transactionId=${transactionId}${
                        showDeleted ? '&showDeleted=true' : ''
                    }`,
                    method: 'GET'
                };
            }
        })
    })
});

export const {
    useGetFeedbackRemovalCreationQuery,
    useGetFeedbackRemovalConfirmationValidationQuery,
    useGetFeedbackRemovalDetailsQuery,
    useGetFeedbackRemovalTransactionQuery,
    useConfirmFeedbackRemovalMutation,
    useDenyFeedbackRemovalMutation,
    useCreateFeedbackRemovalMutation,
    util: feedbackRemovalApiUtil
} = feedbackRemovalApi;
